/**
 * Placement-detail fields shared by the books whose cards plug into the
 * detail panel's net-new sections (slice 4 of the portal→CC merge,
 * ADR-0067): the next-filing-deadline read, the monthly-update cadence,
 * and the parsed monthly-update log feed.
 *
 * Why one shared schema, not four inlined: the four class-B placement
 * fields (`nextFilingDeadline`, `nextFilingDeadlineType`,
 * `lastMonthlyUpdateDate`, `monthlyUpdatesLog`) carry into every book
 * whose panel renders these sections — Placed book, Settled book, Closed,
 * Dropped today. Inlining four nullable fields four times — each with
 * the same docstring — diffuses the contract across the wire schemas.
 * Defining them once and spreading them keeps the rename / retype work
 * a single-file edit.
 *
 * Spec: docs/port-survey/reconciliation.md (evicted 2026-08-29, #775 round 10) (slice 4 — detail panel),
 *       docs/port-survey/cc-2d-data.md (evicted 2026-08-29, #775 round 10) (class-B field list).
 */

import { z } from "zod";
import { LeadAttachmentSchema } from "./lead.js";

/**
 * One parsed entry from the Monthly Updates log. The raw Airtable field
 * is a multilineText with one entry per line in the format
 * `[<ISO-datetime>] <email>: <outcome> — <note>`; the composer parses
 * each line into this shape so the panel renders without re-parsing.
 *
 * `outcome` and `note` are nullable because legacy entries don't always
 * carry both — older firms wrote freeform; the structured format landed
 * with the portal's monthly-update modal in 2026.
 */
export const MonthlyUpdateEntrySchema = z.object({
  /** ISO-8601 datetime parsed from the leading `[…]` bracket. */
  date: z.string(),
  /** Author email (the operator at the HF who filed). */
  author: z.string().nullable(),
  /** Update outcome keyword (e.g. "In treatment", "Demand sent",
   *  "In litigation", "Settled", "No change"). */
  outcome: z.string().nullable(),
  /** Free-text note attached to the update. */
  note: z.string().nullable(),
});
export type MonthlyUpdateEntry = z.infer<typeof MonthlyUpdateEntrySchema>;

/**
 * A downloadable document reference on a placement — the filename to show and
 * the URL to open. Deliberately lean (no size / id): the panel renders it as a
 * filename link exactly the way the portal's FSA chip does. Backs the FSA
 * download; the upload stays a firm-native portal write. Airtable attachment
 * URLs expire (~2h), so this is a snapshot good at load time — the same
 * posture as the existing intake-doc "Documents" links.
 */
export const CaseDocumentSchema = z.object({
  filename: z.string(),
  url: z.string().url(),
});
export type CaseDocument = z.infer<typeof CaseDocumentSchema>;

/**
 * Lead-context panel fields shared by the post-placement books (Placed book,
 * Settled book, Dropped, Closed) — the same "define once, spread with
 * `...`" pattern as PLACEMENT_DETAIL_FIELDS below. Sourced from the joined
 * lead so a placed matter's detail panel keeps its full picture (Aesthetic
 * Parity Phase 2, additive per ADR-0133): the transfer advisory (`signed`),
 * the header "Received {date}" (`receivedDate`), the Case file (`caseFile`),
 * and the FSA download (`fsaDocument`). Spread into each book's case schema
 * so a future field add/rename to this group is a single-file edit.
 */
export const PANEL_DETAIL_LEAD_FIELDS = {
  /** TRUE for a Transfer (already-retained client) — drives the panel's
   *  transfer advisory banner. */
  signed: z.boolean(),
  /** The matter's Creation Date (YYYY-MM-DD) — shown as "Received {date}" in
   *  the panel header. Null when Airtable omits it (or the lead is unresolved). */
  receivedDate: z.string().nullable(),
  /** The running Case file (intake docs) off the joined lead, or []. */
  caseFile: z.array(LeadAttachmentSchema),
  /** The client-signed FSA document (filename + url), or null when none is on
   *  file. Backs the panel's FSA download. */
  fsaDocument: CaseDocumentSchema.nullable(),
} as const;

/**
 * The four class-B placement fields — nullable raw projections. Spread
 * into each book's case schema with `...PLACEMENT_DETAIL_FIELDS`. Note the
 * wire shape carries the PARSED `monthlyUpdates` array (newest-first), not
 * the raw `monthlyUpdatesLog` text — the composer parses the log via
 * `parseMonthlyUpdatesLog` and the raw string is not carried across the wire.
 */
export const PLACEMENT_DETAIL_FIELDS = {
  /** date (YYYY-MM-DD) — next hard-cliff legal deadline (e.g. SOL,
   *  EEOC). Rolls forward as each is satisfied. Null when unset. */
  nextFilingDeadline: z.string().nullable(),
  /** singleSelect label — what kind of deadline (e.g. "Statute of
   *  limitations", "EEOC charge filing (180-day)"). Null when unset. */
  nextFilingDeadlineType: z.string().nullable(),
  /** date (YYYY-MM-DD) — Placements.Last Update, the last day a stamping
   *  write (Monthly Update, fee, settlement, …) touched it; null if none. */
  lastMonthlyUpdateDate: z.string().nullable(),
  /** Parsed monthly-update entries (newest first), or `[]` when
   *  the log is empty / unparsable. */
  monthlyUpdates: z.array(MonthlyUpdateEntrySchema),
} as const;

/**
 * Parse the Airtable `monthlyUpdatesLog` multilineText into a structured
 * array, NEWEST FIRST. The expected format per line:
 *   `[<ISO-datetime>] <email>: <outcome> — <note>`
 *
 * Resilient by design — legacy entries don't always carry every part,
 * so partial matches still surface (date is required; everything else
 * is nullable). Lines that don't carry a parseable leading date are
 * dropped silently (the field is append-only freeform).
 *
 * Exported so composers can share the parse and tests can assert it.
 */
export function parseMonthlyUpdatesLog(
  log: string | null,
): MonthlyUpdateEntry[] {
  if (log === null || log.trim() === "") return [];
  const entries: MonthlyUpdateEntry[] = [];
  // One entry per line — `[<iso>] <author>: <outcome>[ — <note>]`. The
  // outcome / note separator is an em-dash; we tolerate hyphen too in
  // case a hand-edit lost the unicode in transit.
  const lineRe =
    /^\s*\[([^\]]+)\]\s*(?:([^:]+):)?\s*([^—-]+?)?\s*(?:[—-]\s*(.+))?\s*$/;
  for (const raw of log.split(/\r?\n/)) {
    if (raw.trim() === "") continue;
    const m = raw.match(lineRe);
    if (m === null) continue;
    const dateStr = m[1]!.trim();
    // Only keep entries with a parseable date — that's the structural
    // marker for "this line is a real entry."
    if (Number.isNaN(Date.parse(dateStr))) continue;
    entries.push({
      date: dateStr,
      author: m[2] !== undefined ? m[2].trim() : null,
      outcome:
        m[3] !== undefined && m[3].trim() !== "" ? m[3].trim() : null,
      note: m[4] !== undefined && m[4].trim() !== "" ? m[4].trim() : null,
    });
  }
  // Newest first — entries are appended chronologically so reversing
  // is enough; tolerate the rare out-of-order log via timestamp sort.
  entries.sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
  return entries;
}
